/*******************************************************************************
 * Copyright     : Copyright (c)  2026 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for any purpose without the express written permission of Honeywell. *
 *******************************************************************************/

import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  IAvailableSoftwareVersion,
  ISoftwareVersionDownloadArtifact,
  ISoftwareVersionDownloadSasUrlResponse,
  ISoftwareVersionDetails,
} from "../../models/softwaredetails";
import {
  useGetSoftwareVersionDetailsQuery,
  useGetSoftwareVersionDownloadSasUrlQuery,
  useSyncCloudInstallMutation,
} from "../../services/api";
import { downloadLargeFile } from "../../services/file-service-api/fileDownloader";
import SoftwareDetails from "../EBICognition/SoftwareDetails";
import SoftwareDialog from "../SoftwareDialog";
import DownloadFilesDialog, { DownloadFileItem } from "../DownloadFilesDialog";
import { formatFileSize } from "../../utils/utility";
import {
  SoftwareDialogTableHeader,
  SoftwareDialogTableRow,
} from "../../models/softwareDialog";
import { LOCALE_CONST } from "../../utils/constants";
import { useTranslation } from "react-i18next";
import { ContentLoader, ToastContext } from "@forge/common";
import { getErrorInfo } from "../../reusablecomponents/errorInfo/getErrorInfo";
import { NetworkErrObj } from "../gateway-report/utils/gatewayTypes";

interface SoftwareProps {
  selectedAvailableSoftwareVersions: IAvailableSoftwareVersion[];
  installedSoftwareVersion?: string;
  gatewayType?: string;
  deviceId?: string;
  connectivity?: string;
  isLoading?: boolean;
}

const Software: React.FC<SoftwareProps> = ({
  selectedAvailableSoftwareVersions,
  installedSoftwareVersion,
  gatewayType,
  deviceId,
  connectivity,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [selectedVersion, setSelectedVersion] = useState<{
    version: string;
    releaseType: string;
  }>({ version: "", releaseType: "" });
  const [fileToDownload, setFileToDownload] = useState<{
    version: string;
    releaseType: string;
  }>({ version: "", releaseType: "" });
  const [selectedSoftwareVersionDetails, setSelectedSoftwareVersionDetails] =
    useState<ISoftwareVersionDetails>();
  const [selectedSoftwareDownloadDetails, setSelectedSoftwareDownloadDetails] =
    useState<ISoftwareVersionDownloadSasUrlResponse>();
  const [isSoftwareDialogOpen, setIsSoftwareDialogOpen] = useState(false);
  const [isDownloadFilesDialogOpen, setIsDownloadFilesDialogOpen] = useState(false);
  const [softwareDialogMode, setSoftwareDialogMode] = useState<
    "availableVersionDetails" | "details"
  >("details");
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState<
    "error" | "success" | "warning" | "progress"
  >("success");
  const [toastContent, setToastContent] = useState("");
  const convertFn = (value: string) => t(value);
  const { addError, addSuccess, removeAll } = useContext(ToastContext);
  const TIMEOUT_DURATION = 3000;
  const [syncCloudInstall] = useSyncCloudInstallMutation();

  const normalizeDownloadArtifacts = (
    details?: ISoftwareVersionDownloadSasUrlResponse,
  ): ISoftwareVersionDownloadArtifact[] => {
    if (!details) {
      return [];
    }

    if (Array.isArray(details)) {
      return details.filter((item) => Boolean(item?.downloadUrl));
    }

    if (Array.isArray(details.files) && details.files.length > 0) {
      return details.files.filter((item) => Boolean(item?.downloadUrl));
    }

    return details.downloadUrl ? [details] : [];
  };

  const {
    data: softwareVersionDetails,
    error: softwareVersionDetailsError,
    isLoading: softwareVersionDetailsLoading,
  } = useGetSoftwareVersionDetailsQuery(
    {
      gatewayType: gatewayType,
      version: selectedVersion.version,
      releaseType: selectedVersion.releaseType,
    },
    {
      skip:
        !gatewayType ||
        !selectedVersion.version ||
        !selectedVersion.releaseType,
    },
  );

  const {
    data: softwareVersionDownloadSasUrl,
    error: softwareVersionDownloadSasUrlError,
    isLoading: softwareVersionDownloadSasUrlLoading,
  } = useGetSoftwareVersionDownloadSasUrlQuery(
    {
      gatewayType: gatewayType,
      version: fileToDownload.version,
      releaseType: fileToDownload.releaseType,
      deviceId: deviceId,
    },
    {
      skip:
        !gatewayType || !fileToDownload.version || !fileToDownload.releaseType,
    },
  );

  // Clear state when parameters change
  useEffect(() => {
    setSelectedSoftwareVersionDetails(undefined);
  }, [selectedVersion.version, selectedVersion.releaseType]);

  useEffect(() => {
    // Handle errors first to prevent showing cached data from previous successful calls
    if (softwareVersionDetailsError) {
      setSelectedSoftwareVersionDetails(undefined);
    } else if (softwareVersionDetails !== undefined && !softwareVersionDetailsLoading) {
      setSelectedSoftwareVersionDetails(softwareVersionDetails);
    }
  }, [softwareVersionDetails, softwareVersionDetailsError, softwareVersionDetailsLoading]);

  useEffect(() => {
    if (softwareVersionDownloadSasUrl) {
      setSelectedSoftwareDownloadDetails(softwareVersionDownloadSasUrl);
      setIsDownloadFilesDialogOpen(true);
    } else if (softwareVersionDownloadSasUrlError) {
      displayErrorToast(
        (softwareVersionDownloadSasUrlError as NetworkErrObj)?.status,
        getErrorObjValue(softwareVersionDownloadSasUrlError as NetworkErrObj),
        false,
      );
      setFileToDownload({ version: "", releaseType: "" });
    }
  }, [softwareVersionDownloadSasUrl, softwareVersionDownloadSasUrlError]);

  // Downloadable files shown in the download links dialog
  const downloadFileItems = useMemo<DownloadFileItem[]>(() => {
    const artifacts = normalizeDownloadArtifacts(selectedSoftwareDownloadDetails);
    if (artifacts.length === 0) return [];

    const items: DownloadFileItem[] = [];
    artifacts.forEach((artifact, index) => {
      const primaryFileName = artifact.fileName || fileToDownload.version;
      items.push({
        id: `tar-file-${index}`,
        sectionLabel: t(LOCALE_CONST.downloadSoftwareSection),
        primaryText: artifact.version || fileToDownload.version,
        secondaryText: artifact.sizeFormatted,
        fileName: primaryFileName,
        downloadUrl: artifact.downloadUrl,
      });

      if (artifact.metadataDownloadUrl) {
        const metadataFileName = `${primaryFileName}.meta`;
        items.push({
          id: `metadata-file-${index}`,
          sectionLabel: t(LOCALE_CONST.downloadValidationFileSection),
          primaryText: metadataFileName,
          fileName: metadataFileName,
          downloadUrl: artifact.metadataDownloadUrl,
        });
      }
    });

    return items;
  }, [selectedSoftwareDownloadDetails, fileToDownload.version, t]);

  const handleDialogFileDownload = async (file: DownloadFileItem) => {
    const { error } = await downloadLargeFile(file.downloadUrl, file.fileName);
    if (error) {
      showErrorToast(error);
    }
  };

  const handleDownloadDialogClose = () => {
    setIsDownloadFilesDialogOpen(false);
    setFileToDownload({ version: "", releaseType: "" });
  };

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
        removeAll();
      }, TIMEOUT_DURATION);

      return () => clearTimeout(timer);
    }
  }, [showToast, toastType]);

  const displayErrorToast = (
    statusCode: number,
    errMsg?: string,
    fetchConfig?: boolean,
  ) => {
    const errObj = getErrorInfo(statusCode, convertFn);
    if (!fetchConfig) {
      showErrorToast(errMsg || errObj.description);
    } else {
      showErrorToast(t(LOCALE_CONST.noRespReceivedSystem));
    }
  };

  const getErrorObjValue = (error: NetworkErrObj) => {
    if (!error) {
      return "";
    } else if (typeof error.data === "object") {
      return error.data?.Message || "";
    } else if (typeof error.data === "string") {
      return error.data || "";
    } else {
      return "";
    }
  };

  const showErrorToast = (message: string) => {
    setToastType("error");
    setToastContent(message);
    setShowToast(true);
  };

  const showSuccessToast = (message: string) => {
    setToastType("success");
    setToastContent(message);
    setShowToast(true);
  };

  const handleSaveChanges = async (version: string) => {
    if (!deviceId || !version) {
      return;
    }

    try {
      await syncCloudInstall({
        deviceId,
        version,
        status: "installed",
      }).unwrap();
      showSuccessToast(t(LOCALE_CONST.softwareVersionUpdated));
    } catch (error) {
      displayErrorToast(
        (error as NetworkErrObj)?.status,
        getErrorObjValue(error as NetworkErrObj),
        false,
      );
    }
  };

  useEffect(() => {
    if (showToast) {
      if (toastType === "error") {
        addError({
          id: "uop-toast-message",
          className: "uop-toast-message",
          title: "",
          subtitle: "",
          showClose: true,
          showDismiss: false,
          content: toastContent,
        });
      } else if (toastType === "success") {
        addSuccess({
          id: "uop-toast-message",
          className: "uop-toast-message",
          title: "",
          subtitle: "",
          showClose: true,
          showDismiss: false,
          content: toastContent,
        });
      }
    }
  }, [showToast, toastType, toastContent, addError, addSuccess]);

  // Transform software version details into table format for SoftwareDialog
  const softwareDialogTableHeaders = useMemo<SoftwareDialogTableHeader[]>(
    () => [
      { id: "component", content: t(LOCALE_CONST.component) },
      { id: "version", content: t(LOCALE_CONST.version) },
    ],
    [t],
  );

  const softwareDialogTableData = useMemo<SoftwareDialogTableRow[]>(() => {
    if (!selectedSoftwareVersionDetails?.components) return [];
    return selectedSoftwareVersionDetails.components.map((item, index) => ({
      id: `row-${index}`,
      cell: [{ content: item.component }, { content: item.version }],
    }));
  }, [selectedSoftwareVersionDetails]);

  return (
    <>
      <div className="mt-4">
        {isLoading && <ContentLoader />}
        {!isLoading && (
          <SoftwareDetails
            availableSoftwareVersions={selectedAvailableSoftwareVersions}
            installedSoftwareVersion={installedSoftwareVersion}
            isCreate={false}
            isDisabled={connectivity === "Connected"}
            showDownload={true}
            isConnected={connectivity === "Connected"}
            onDownload={(version, releaseType) => {
              setFileToDownload({ version, releaseType });
            }}
            onViewDetails={(version, releaseType) => {
              setSelectedVersion({ version, releaseType });
              setSoftwareDialogMode("details");
              setIsSoftwareDialogOpen(true);
            }}
            onSaveChanges={(version) => {
              handleSaveChanges(version);
            }}
            onVersionCardClick={(version, releaseType) => {
              setSelectedVersion({ version, releaseType });
              setSoftwareDialogMode("availableVersionDetails");
              setIsSoftwareDialogOpen(true);
            }}
          />
        )}
      </div>

      <SoftwareDialog
        isOpen={isSoftwareDialogOpen}
        onClose={() => setIsSoftwareDialogOpen(false)}
        softwareVersion={selectedVersion.version}
        downloadSize={formatFileSize(
          selectedSoftwareVersionDetails?.size || "0",
        )}
        tableHeaders={softwareDialogTableHeaders}
        tableData={softwareDialogTableData}
        showFooterText={softwareDialogMode === "availableVersionDetails"}
        showDownload={false}
      />

      <DownloadFilesDialog
        isOpen={isDownloadFilesDialogOpen}
        onClose={handleDownloadDialogClose}
        title={t(LOCALE_CONST.downloadImageTitle)}
        description={t(LOCALE_CONST.downloadImageDescription)}
        files={downloadFileItems}
        downloadLabel={t(LOCALE_CONST.downloadLink)}
        onDownload={handleDialogFileDownload}
      />
    </>
  );
};

export default Software;
